// Display helper functions for the DossTable selection checkboxes.

import { GridRowId } from '@mui/x-data-grid-premium'

// A per-row checkbox should be checked when `selectAll` is active OR the row is individually selected.
export const getRowCheckboxChecked = (isSelectAll: boolean, isRowSelected: boolean): boolean =>
  isSelectAll || isRowSelected

export const getHeaderCheckboxState = (
  isSelectAll: boolean,
  loadedRowIds: GridRowId[],
  selectedRowIds: GridRowId[],
): { checked: boolean; indeterminate: boolean } => {
  // In selectAll mode, the header checkbox is always checked and never indeterminate, regardless
  // of the loaded page.
  if (isSelectAll) {
    return { checked: true, indeterminate: false }
  }

  const selectedIds = new Set(selectedRowIds)
  const selectedLoadedCount = loadedRowIds.filter((id) => selectedIds.has(id)).length

  const allLoadedSelected = loadedRowIds.length > 0 && selectedLoadedCount === loadedRowIds.length
  // Indeterminate only when SOME but NOT ALL are selected.
  const someLoadedSelected = selectedLoadedCount > 0

  return { checked: allLoadedSelected, indeterminate: someLoadedSelected && !allLoadedSelected }
}

/**
 * Whether `selectAll` mode should remain selected after a MUI selection-model change. Only
 * relevant when `selectAll` is checked and we want to confirm it should still be so.
 *
 * Right now, we track `selectAll` separately from MUI's native selection model.
 * We want to keep them in sync very closely, so we can pass proper parameters to bulk actions.
 *
 * This can also potentially get removed if/when we upgrade to MUI v8.
 */
export const shouldKeepSelectAll = (
  isSelectAll: boolean,
  loadedRowIds: GridRowId[],
  newSelectedRowIds: GridRowId[],
): boolean => {
  if (!isSelectAll) {
    return false
  }

  // `every` is vacuously true on an empty page, which would keep select-all set over a grid that
  // has no rows loaded yet (see the apiRef init race in DossTable's renderHeader).
  if (loadedRowIds.length === 0) {
    return false
  }

  // MUI uses server pagination which replaces the `rows` per page, so the size here is bounded,
  // on the order of tens of IDs (~50) even on large tables.
  const selectedIds = new Set(newSelectedRowIds)
  return loadedRowIds.every((id) => selectedIds.has(id))
}
